export interface OutreachTouch {
  touchNumber: number;
  dayOffset: number;
  channel: 'linkedin_dm' | 'email' | 'linkedin_engage';
  subject: string | null;
  hook: string;
  template: string;
}

export interface OutreachSequence {
  segment: string;
  touches: OutreachTouch[];
}

export const OUTREACH_SEQUENCES: OutreachSequence[] = [
  {
    segment: 'ai_data_consultancy',
    touches: [
      {
        touchNumber: 1,
        dayOffset: 0,
        channel: 'linkedin_dm',
        subject: null,
        hook: 'Your team delivers AI — but who upskills them?',
        template: 'Hi [Name], I see [Company] is doing great work in AI consulting. I help firms like yours sharpen their delivery bench — workshops on scoping AI projects, prompt engineering for client work, and building evaluation frameworks. Would a quick chat make sense?',
      },
      {
        touchNumber: 2,
        dayOffset: 3,
        channel: 'email',
        subject: 'Quick question about your AI delivery bench',
        hook: 'Quick question about your AI delivery bench',
        template: 'Hi [Name], I help AI consultancies accelerate delivery without hiring full-time. Think: workshops on scoping, prompt engineering, and evaluation frameworks your team can use on client projects immediately. Would 15 minutes make sense to see if there\'s a fit?',
      },
      {
        touchNumber: 3,
        dayOffset: 7,
        channel: 'linkedin_engage',
        subject: null,
        hook: 'Like/comment on their content with genuine insight',
        template: 'Like/comment on their content with genuine insight. Stay top of mind.',
      },
      {
        touchNumber: 4,
        dayOffset: 14,
        channel: 'email',
        subject: 'The upskilling gap in AI delivery',
        hook: 'The upskilling gap in AI delivery',
        template: 'Hi [Name], one pattern I keep seeing: AI consultancies win the work, but the bench isn\'t always deep enough to deliver it confidently. I run targeted workshops that get your team delivering faster — scoping, evaluation, production patterns. Happy to share what\'s worked for similar firms.',
      },
      {
        touchNumber: 5,
        dayOffset: 28,
        channel: 'linkedin_dm',
        subject: null,
        hook: 'Circle back — soft close',
        template: 'Hey [Name], wanted to circle back. If upskilling your delivery team on AI isn\'t a priority right now, no worries — but if it is, I\'d love to share what\'s working for firms like yours.',
      },
    ],
  },
  {
    segment: 'staff_aug',
    touches: [
      {
        touchNumber: 1,
        dayOffset: 0,
        channel: 'linkedin_dm',
        subject: null,
        hook: 'Helping your AI bench deliver faster',
        template: 'Hi [Name], I noticed [Company] places AI/data talent. I help staff-aug firms add a training layer so placed engineers ramp faster and deliver more confidently. Clients notice. Worth a quick chat?',
      },
      {
        touchNumber: 2,
        dayOffset: 3,
        channel: 'email',
        subject: 'A training add-on your clients would pay for',
        hook: 'A training add-on your clients would pay for',
        template: 'Hi [Name], what if you could offer your clients not just AI talent, but AI talent that\'s been trained on production patterns, evaluation frameworks, and scoping methodology? I help staff-aug firms build that capability. Quick call?',
      },
      {
        touchNumber: 3,
        dayOffset: 14,
        channel: 'email',
        subject: 'Quick ROI math on bench training',
        hook: 'Quick ROI math on bench training',
        template: 'Hi [Name], quick thought: if training your bench on AI patterns and evaluation cuts ramp time by even 2 weeks per placement, what\'s that worth across 10 placements? I help firms like [Company] build exactly that. Happy to walk through the math.',
      },
    ],
  },
  {
    segment: 'bank_credit_union',
    touches: [
      {
        touchNumber: 1,
        dayOffset: 0,
        channel: 'email',
        subject: 'AI governance training for [Company]\'s team',
        hook: 'AI governance training for banking team',
        template: 'Hi [Name], I saw [Company]\'s work with [AI tool / press mention]. Impressive. The next challenge most banks face: making sure the team around the AI can evaluate, govern, and explain it — especially with examiners watching. I run workshops specifically for banking teams on AI literacy, governance, and responsible adoption. Worth a conversation?',
      },
      {
        touchNumber: 2,
        dayOffset: 7,
        channel: 'linkedin_dm',
        subject: null,
        hook: 'Follow up on AI workshops for banking teams',
        template: 'Hi [Name], just following up on my note about AI workshops for banking teams. I\'ve helped institutions bridge the gap between \'we bought an AI tool\' and \'our people actually know how to use and govern it.\' Happy to share more.',
      },
      {
        touchNumber: 3,
        dayOffset: 21,
        channel: 'email',
        subject: 'Quick resource: AI readiness checklist for banks',
        hook: 'AI readiness checklist for banks',
        template: 'Hi [Name], I put together a short AI readiness checklist for bank teams rolling out AI tools — covers literacy gaps, governance needs, and examiner prep. Happy to send it over, no strings. Would that be useful?',
      },
    ],
  },
  {
    segment: 'insurer',
    touches: [
      {
        touchNumber: 1,
        dayOffset: 0,
        channel: 'email',
        subject: 'AI underwriting training + VA compliance alignment',
        hook: 'AI underwriting training + VA compliance',
        template: 'Hi [Name], I noticed [Company]\'s recent adoption of AI analytics for underwriting. Virginia\'s Admin Letter 2024-01 is raising the bar on documentation, fairness testing, and human oversight. I help insurance teams build the internal capability to evaluate, document, and govern AI systems — so you\'re ready when the Bureau asks.',
      },
      {
        touchNumber: 2,
        dayOffset: 10,
        channel: 'linkedin_dm',
        subject: null,
        hook: 'VA AI guidance follow-up',
        template: 'Hi [Name], Virginia\'s AI guidance for insurers is raising the bar on documentation and oversight. I help carriers build internal capability to evaluate and govern AI underwriting tools. Worth a quick conversation?',
      },
    ],
  },
  {
    segment: 'it_integrator',
    touches: [
      {
        touchNumber: 1,
        dayOffset: 0,
        channel: 'linkedin_dm',
        subject: null,
        hook: 'Bridge from "we say AI" to "we deliver AI"',
        template: 'Hi [Name], I see [Company] is building out AI capabilities. I help IT integrators bridge the gap from \'we say we do AI\' to \'we actually deliver AI\' — workshops on scoping, evaluation, and production patterns. Quick chat?',
      },
      {
        touchNumber: 2,
        dayOffset: 7,
        channel: 'email',
        subject: 'Adding AI delivery depth to your team',
        hook: 'Adding AI delivery depth to your team',
        template: 'Hi [Name], the firms winning AI work right now aren\'t just hiring — they\'re upskilling. I run targeted workshops that get delivery teams confident on AI scoping, evaluation, and production patterns. Would 15 minutes make sense?',
      },
      {
        touchNumber: 3,
        dayOffset: 21,
        channel: 'email',
        subject: 'AI delivery capability — quick question',
        hook: 'AI delivery capability — quick question',
        template: 'Hi [Name], circling back on building AI delivery capability at [Company]. If your team is taking on AI projects, I can help them deliver with more confidence. Happy to share what\'s working for similar firms.',
      },
    ],
  },
];

export function getSequenceForSegment(segment: string): OutreachSequence | undefined {
  return OUTREACH_SEQUENCES.find(s => s.segment === segment);
}

export function fillTemplate(template: string, company: string, contactName: string | null): string {
  let filled = template.replace(/\[Company\]/g, company).replace(/\[Bank Name\]/g, company);
  if (contactName) {
    filled = filled.replace(/\[Name\]/g, contactName);
  }
  return filled;
}
